import React, { useMemo } from "react";

import { INextGenDashboardChart } from "../../services/zeos-api/contracts/Dashboard";

interface IProps {
  chart: INextGenDashboardChart;
}

// Monday-first rows, as in the mid-fi sheet. Values stay keyed by the API's 0=Sunday dayOfWeek.
const DAYS = [
  { dayOfWeek: 1, label: "Mon" },
  { dayOfWeek: 2, label: "Tue" },
  { dayOfWeek: 3, label: "Wed" },
  { dayOfWeek: 4, label: "Thu" },
  { dayOfWeek: 5, label: "Fri" },
  { dayOfWeek: 6, label: "Sat" },
  { dayOfWeek: 0, label: "Sun" },
];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Mid-fi low / med / high scale (UX-05); empty cells use the page background.
const LEVELS = [
  { label: "low", className: "bg-mf-energy-soft" },
  { label: "med", className: "bg-mf-energy-md" },
  { label: "high", className: "bg-mf-energy" },
];

const levelIndex = (value: number, max: number) => {
  if (max <= 0 || value <= 0) {
    return -1;
  }
  return Math.min(LEVELS.length - 1, Math.floor((value / max) * LEVELS.length));
};

const formatHour = (hour: number) => {
  if (hour === 12) {
    return "Noon";
  }
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour} ${hour < 12 ? "AM" : "PM"}`;
};

// Header row, then one row per day; the peak-window label sits in the row after the last day.
const HEADER_ROW = 1;
const FIRST_DAY_ROW = 2;
const LABEL_ROW = FIRST_DAY_ROW + DAYS.length;
const hourColumn = (hour: number) => hour + 2;

export const DEFAULT_HEATMAP_EMPTY_MESSAGE =
  "Requires session / interval data. Placeholder until the feed connects.";

// Utilization by Hour × Day (4019, mid-fi sheet 03). Every cell is placed explicitly so the peak
// window outline can overlay its hour columns. With no cells, the grid still renders (faded) with
// an overlay naming what's missing, per Alan (9/30): show the richness even before the data exists.
export const NextGenHeatmap = ({ chart }: IProps) => {
  const cells = chart.heatmapCells ?? [];
  const isEmpty = cells.length === 0;
  const unit = chart.heatmapUnit || "sessions";
  const highlight = chart.heatmapHighlight;

  const valueByCell = useMemo(() => {
    const map = new Map<string, number>();
    cells.forEach((cell) => map.set(`${cell.dayOfWeek}-${cell.hour}`, cell.value));
    return map;
  }, [cells]);

  const maxValue = useMemo(() => cells.reduce((max, cell) => Math.max(max, cell.value), 0), [cells]);

  return (
    <div>
      <div className="relative overflow-x-auto">
        <div
          className={`grid min-w-[640px] gap-[3px] ${isEmpty ? "opacity-40" : ""}`}
          style={{ gridTemplateColumns: "40px repeat(24, minmax(0, 1fr))" }}
          aria-hidden={isEmpty ? "true" : undefined}
        >
          {HOURS.map((hour) => (
            <div
              key={`h-${hour}`}
              className="text-center text-[9.5px] font-bold text-mf-faint"
              style={{ gridRow: HEADER_ROW, gridColumn: hourColumn(hour) }}
            >
              {hour % 3 === 0 ? formatHour(hour) : ""}
            </div>
          ))}
          {DAYS.map((day, dayIndex) => (
            <React.Fragment key={day.label}>
              <div
                className="flex items-center text-[10px] font-bold uppercase text-mf-faint"
                style={{ gridRow: FIRST_DAY_ROW + dayIndex, gridColumn: 1 }}
              >
                {day.label}
              </div>
              {HOURS.map((hour) => {
                const value = valueByCell.get(`${day.dayOfWeek}-${hour}`) ?? 0;
                const level = levelIndex(value, maxValue);
                return (
                  <div
                    key={hour}
                    title={isEmpty ? undefined : `${day.label} ${formatHour(hour)}: ${value} ${unit}`}
                    className={`h-6 rounded-[3px] ${level < 0 ? "bg-mf-bg" : LEVELS[level].className}`}
                    style={{ gridRow: FIRST_DAY_ROW + dayIndex, gridColumn: hourColumn(hour) }}
                  />
                );
              })}
            </React.Fragment>
          ))}
          {highlight ? (
            <>
              <div
                data-testid="heatmap-highlight"
                className="pointer-events-none -m-[2px] rounded border-2 border-dashed border-mf-alert"
                style={{
                  gridRow: `${FIRST_DAY_ROW} / ${LABEL_ROW}`,
                  gridColumn: `${hourColumn(highlight.startHour)} / ${hourColumn(highlight.endHour)}`,
                }}
              />
              <div
                className="pt-1 text-center text-[10px] font-extrabold uppercase tracking-wide text-mf-orange-ink"
                style={{
                  gridRow: LABEL_ROW,
                  gridColumn: `${hourColumn(highlight.startHour)} / ${hourColumn(highlight.endHour)}`,
                }}
              >
                {highlight.label}
              </div>
            </>
          ) : null}
        </div>
        {isEmpty ? (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="max-w-sm rounded-lg border border-dashed border-mf-faint bg-white/90 px-4 py-2 text-center text-xs font-bold text-mf-sub">
              {chart.emptyMessage || DEFAULT_HEATMAP_EMPTY_MESSAGE}
            </div>
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-[18px] text-[11px] font-bold text-mf-sub">
        {LEVELS.map((level) => (
          <span key={level.label} className="flex items-center gap-1.5">
            <span className={`h-3 w-3 rounded-[3px] ${level.className}`} aria-hidden="true" />
            {level.label}
          </span>
        ))}
        <span className="text-mf-faint">{unit} per hour</span>
      </div>
    </div>
  );
};
